import React, { useState } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  List,
  Datagrid,
  TextField,
  Show,
  TextInput,
  Filter,
  TabbedShowLayout,
  ReferenceManyField,
  Tab,
  SelectInput,
  DateInput,
  FunctionField
} from "react-admin";
import { exportToExcel } from "../common";


const PointHistoryModal = ({ userId, userName, onClose }) => {
  const [transactions, setTransactions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(process.env.REACT_APP_BASE_URL + "admin/wallet-points/user/" + userId, {
      headers: { authorization: token }
    })
      .then(r => r.json())
      .then(data => { setTransactions(data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId]);

  const formatDate = ts => {
    if (!ts) return "-";
    try { return new Date(ts).toLocaleString("en-IN", {day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}); }
    catch(e) { return "-"; }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#fff",borderRadius:12,width:"90%",maxWidth:700,maxHeight:"80vh",overflow:"auto",padding:24,position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:16,background:"none",border:"none",fontSize:22,cursor:"pointer"}}>×</button>
        <h3 style={{marginBottom:16}}>Point History — {userName}</h3>
        {loading ? <div>Loading...</div> : (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:"#f8f9fa"}}>
                {["Date","Points","Type","Description"].map(h => (
                  <th key={h} style={{padding:"8px 12px",textAlign:"left",borderBottom:"2px solid #dee2e6"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr><td colSpan={4} style={{padding:20,textAlign:"center",color:"#888"}}>No transactions found</td></tr>
              ) : transactions.map((t, i) => (
                <tr key={i} style={{borderBottom:"1px solid #f0f0f0",background:i%2===0?"#fff":"#fafafa"}}>
                  <td style={{padding:"8px 12px",whiteSpace:"nowrap"}}>{formatDate(t.transactionDate)}</td>
                  <td style={{padding:"8px 12px",fontWeight:700,color:t.transactionType==="add"?"#28a745":"#dc3545"}}>
                    {t.transactionType==="add"?"+":"-"}{t.points}
                  </td>
                  <td style={{padding:"8px 12px"}}>
                    <span style={{padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:600,background:t.transactionType==="add"?"#d4edda":"#f8d7da",color:t.transactionType==="add"?"#155724":"#721c24"}}>{t.transactionType}</span>
                  </td>
                  <td style={{padding:"8px 12px"}}>{t.transactionDescripion || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const PointHistoryButton = ({ record }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontWeight:600}}>{record?.referrInfo?.wallet ?? 0}</span>
      <button
        onClick={e => { e.stopPropagation(); setOpen(true); }}
        style={{padding:"2px 8px",fontSize:11,borderRadius:4,border:"1px solid #007bff",background:"#007bff",color:"#fff",cursor:"pointer"}}
      >History</button>
      {open && <PointHistoryModal userId={record._id} userName={record.legalName} onClose={() => setOpen(false)} />}
    </div>
  );
};

const exporter = users => {
  const usersForExport = users.map(user => {
    return {
      Continent: user.continent,
      Country: user.country,
      "Legal name": user.legalName,
      Email: user.email,
      "Registered on": user.registeredOn,
      Phone: user.phone,
      dob: user.dob,
      Source: user.source,
      Points: user.referrInfo?.wallet ?? 0,  // ✅ added
    };
  });
  exportToExcel(usersForExport, "users");
};

export const UserShow = props => {
  return (
    <Show title="User details" {...props}>
      <TabbedShowLayout>
        <Tab label="General Details">
          <TextField source="continent" />
          <TextField source="country" />
          <TextField source="legalName" />
          <TextField source="referralCode" />
          <TextField source="email" />
          <TextField source="phone" />
          <TextField source="dob" />
          <FunctionField
            label="Wallet Points"
            render={record => record?.referrInfo?.wallet ?? 0}
          />
        </Tab>
        <Tab label="Purchases">
          <ReferenceManyField
            reference="users"
            target="user_id"
            addLabel={false}
          >
            <Datagrid>
              <TextField label="Donation name" source="paymentName" />
              <TextField label="Donation type" source="paymentType" />
              <TextField label="Donation status" source="paymentStatus" />
              <TextField label="Donation amount" source="amount" />
              <TextField label="Total EMI" source="totalEmi" />
              <TextField label="EMI left" source="emiLeft" />
            </Datagrid>
          </ReferenceManyField>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const UserFilter = props => {
  return (
    <Filter {...props}>
      <TextInput label="Search" source="userName" alwaysOn />
      <SelectInput
        alwaysOn
        allowEmpty={false}
        source="userStatus"
        choices={[
          { id: "all", name: "All" },
          { id: "active", name: "Active" },
          { id: "inactive", name: "Inactive" }
        ]}
      />
      <DateInput alwaysOn label="From Date" source="fromDate" />
      <DateInput alwaysOn label="To Date" source="toDate" />
    </Filter>
  );
};

export default props => (
  <List
    filters={<UserFilter />}
    exporter={exporter}
    bulkActionButtons={false}
    {...props}
  >
    <Datagrid rowClick="show">
      <TextField sortable={false} source="continent" />
      <TextField sortable={false} source="country" />
      <TextField sortable={false} source="legalName" />
      <TextField sortable={false} source="initiatedName" label="Initiated Name" />
      <TextField sortable={false} source="email" />
      <TextField sortable={false} source="registeredOn" />
      <TextField sortable={false} source="phone" />
      <TextField sortable={false} source="dob" />
      <TextField sortable={false} source="source" />
      <FunctionField
        sortable={false}
        label="Points"
        render={record => <PointHistoryButton record={record} />}
      />
    </Datagrid>
  </List>
);